import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { AuthService } from '@core/auth.service';
import { LpProjectSettingsService } from '@core/lp/lp-project-settings.service';
import { Slicer } from '@features/styles/sprite-sheet.utils';
import { Rect, Vector2 } from '@typings/vectors';

export type TSprite = {
  url: string;
  size: Vector2;
  paddingBox: Rect;
  contentBox: Rect;
  scale: number;
};

@Injectable({
  providedIn: 'root',
})
export class SpriteSheetService {
  public readonly SPRITE_BUTTON_PRIMARY = signal<TSprite | undefined>(undefined);
  public readonly SPRITE_BUTTON_PRIMARY_ROUND = signal<TSprite | undefined>(undefined);
  public readonly SPRITE_BUTTON_SECONDARY = signal<TSprite | undefined>(undefined);
  public readonly SPRITE_BUTTON_DISABLED = signal<TSprite | undefined>(undefined);
  public readonly SPRITE_BUTTON_ICON = signal<TSprite | undefined>(undefined);
  public readonly SPRITE_BUTTON_ICON_HIGHLIGHT = signal<TSprite | undefined>(undefined);
  public readonly SPRITE_TAB_NORMAL = signal<TSprite | undefined>(undefined);
  public readonly SPRITE_TAB_OPEN = signal<TSprite | undefined>(undefined);
  public readonly SPRITE_PANEL_PRIMARY = signal<TSprite | undefined>(undefined);
  public readonly SPRITE_PANEL_SECONDARY = signal<TSprite | undefined>(undefined);
  public readonly SPRITE_SCROLLBAR_BACKGROUND = signal<TSprite | undefined>(undefined);
  public readonly SPRITE_SCROLLBAR_HANDLE = signal<TSprite | undefined>(undefined);
  public readonly SPRITE_ICON_INVENTORY = signal<TSprite | undefined>(undefined);
  public readonly SPRITE_ICON_CROSS = signal<TSprite | undefined>(undefined);
  public readonly SPRITE_ICON_ARROW = signal<TSprite | undefined>(undefined);
  public readonly SPRITE_PANEL_SLOT = signal<TSprite | undefined>(undefined);
  public readonly SPRITE_PANEL_SLOT_HIGHLIGHTED = signal<TSprite | undefined>(undefined);
  public readonly SPRITE_PANEL_INVENTORY = signal<TSprite | undefined>(undefined);
  public readonly SPRITE_HEADER_PRIMARY = signal<TSprite | undefined>(undefined);
  public readonly SPRITE_BUTTON_SUCCESS = signal<TSprite | undefined>(undefined);
  public readonly SPRITE_BUTTON_DANGER = signal<TSprite | undefined>(undefined);
  readonly #lpProjectSettingsService = inject(LpProjectSettingsService);
  readonly #auth = inject(AuthService);

  constructor() {
    const config = [
      ['SPRITE_BUTTON_PRIMARY', [1, 1], this.SPRITE_BUTTON_PRIMARY],
      ['SPRITE_BUTTON_PRIMARY_ROUND', [257, 1], this.SPRITE_BUTTON_PRIMARY_ROUND],
      ['SPRITE_BUTTON_SECONDARY', [1, 257], this.SPRITE_BUTTON_SECONDARY],
      ['SPRITE_BUTTON_DISABLED', [1, 513], this.SPRITE_BUTTON_DISABLED],
      ['SPRITE_BUTTON_ICON', [783, 0], this.SPRITE_BUTTON_ICON],
      ['SPRITE_BUTTON_ICON_HIGHLIGHT', [1306, 0], this.SPRITE_BUTTON_ICON_HIGHLIGHT],
      ['SPRITE_TAB_NORMAL', [2350, 261], this.SPRITE_TAB_NORMAL],
      ['SPRITE_TAB_OPEN', [2611, 261], this.SPRITE_TAB_OPEN],
      ['SPRITE_PANEL_PRIMARY', [1, 1025], this.SPRITE_PANEL_PRIMARY],
      ['SPRITE_PANEL_SECONDARY', [1, 1281], this.SPRITE_PANEL_SECONDARY],
      ['SPRITE_SCROLLBAR_BACKGROUND', [1044, 0], this.SPRITE_SCROLLBAR_BACKGROUND],
      ['SPRITE_SCROLLBAR_HANDLE', [1175, 0], this.SPRITE_SCROLLBAR_HANDLE],
      ['SPRITE_ICON_INVENTORY', [1089, 769], this.SPRITE_ICON_INVENTORY],
      ['SPRITE_ICON_ARROW', [1409, 769], this.SPRITE_ICON_ARROW],
      ['SPRITE_ICON_CROSS', [929, 769], this.SPRITE_ICON_CROSS],
      ['SPRITE_PANEL_SLOT', [513, 513], this.SPRITE_PANEL_SLOT],
      ['SPRITE_PANEL_SLOT_HIGHLIGHTED', [769, 513], this.SPRITE_PANEL_SLOT_HIGHLIGHTED],
      ['SPRITE_PANEL_INVENTORY', [1025, 513], this.SPRITE_PANEL_INVENTORY],
      ['SPRITE_HEADER_PRIMARY', [1, 1537], this.SPRITE_HEADER_PRIMARY],
      ['SPRITE_BUTTON_SUCCESS', [1, 769], this.SPRITE_BUTTON_SUCCESS],
      ['SPRITE_BUTTON_DANGER', [257, 769], this.SPRITE_BUTTON_DANGER],
    ] as [name: string, pos: Vector2, signal: WritableSignal<TSprite | undefined>][];

    const load = async () => {
      await this.#auth.waitForLogin();
      const projectSettings = await this.#lpProjectSettingsService.getSettings();
      const mainUrl = projectSettings.styles.sprites.url;
      const sizesUrl = projectSettings.styles.sizes.url;

      const [styleSheetImg, styleSheetSizingImg] = await Promise.all([
        this.loadImage(mainUrl), //
        this.loadImage(sizesUrl),
      ]);
      const slicer = new Slicer(styleSheetImg, styleSheetSizingImg);
      await Promise.all(
        config.map(async ([name, pos, signal]) => {
          try {
            const sprite = await slicer.getSprite(pos);
            console.log(name, sprite);
            signal.set(sprite);
          } catch (e) {
            console.warn(`An error occured when reading sprite '${name}' (using error/fallback sprite instead):`, e);
            const fallbackSprite: TSprite = {
              url: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAIAAAACABAMAAAAxEHz4AAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAJUExURedD/wAS//KrKZU0Ej8AAAAJcEhZcwAADsMAAA7DAcdvqGQAAAAYdEVYdFNvZnR3YXJlAFBhaW50Lk5FVCA1LjEuOBtp6qgAAAC2ZVhJZklJKgAIAAAABQAaAQUAAQAAAEoAAAAbAQUAAQAAAFIAAAAoAQMAAQAAAAIAAAAxAQIAEAAAAFoAAABphwQAAQAAAGoAAAAAAAAAYAAAAAEAAABgAAAAAQAAAFBhaW50Lk5FVCA1LjEuOAADAACQBwAEAAAAMDIzMAGgAwABAAAAAQAAAAWgBAABAAAAlAAAAAAAAAACAAEAAgAEAAAAUjk4AAIABwAEAAAAMDEwMAAAAACrgCETU544KAAAADtJREFUaN7tzDEBACAIADAqWIH+Ifl9hBe3AIsAuJwhgUCwP8gHgUAgEAgEAoFAIBAIBIIugUCwNwC+V0VwuhAXZGxZAAAAAElFTkSuQmCC',
              size: [128, 128] as Vector2,
              scale: 1,
              contentBox: { from: [32, 32] as Vector2, to: [95, 95] as Vector2 },
              paddingBox: { from: [16, 16] as Vector2, to: [111, 111] as Vector2 },
            };
            signal.set(fallbackSprite);
          }
        }),
      );
    };
    load();
  }

  private async loadImage(url: string): Promise<HTMLImageElement> {
    return new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.crossOrigin = 'Anonymous';
      image.src = url;
    });
  }
}
